// Pattern compilation logic - ported from original concat-logic.js
import {
  DEFAULT_SYMBOL,
  getSymbolDetails,
  getReverseSymbol,
} from "../utils/stitchSymbols";

/**
 * Compile a chart grid into readable paragraph instructions
 * @param {Object} chart - Chart object with height, width, and rows
 * @param {boolean} abbreviate - If true, use abbreviations instead of full names
 * @param {boolean} inTheRound - If true, all rows are right-side rows (no symbol translation)
 * @returns {Array} Array of instruction objects with rowLabel and instruction properties
 */
export const compilePattern = (chart, abbreviate = false, inTheRound = false) => {
  if (!chart || !chart.rows) {
    return [];
  }

  const { height, width } = chart;
  const rowInstructions = [];

  // Process each row separately
  // According to knitting chart conventions:
  // Flat knitting:
  // - Odd rows (1, 3, 5...) = Right Side (RS): read right to left
  // - Even rows (2, 4, 6...) = Wrong Side (WS): read left to right, symbols translated
  // In the round:
  // - All rows are right-side rows: read right to left, no symbol translation
  for (let row = 0; row < height; row++) {
    const rowKey = `row${row}`;
    const rowData = chart.rows[rowKey] || Array(width).fill(DEFAULT_SYMBOL);
    
    // For in-the-round: all rows are treated as right-side rows
    // For flat knitting: determine if this is an odd (RS) or even (WS) row (1-indexed)
    const rowNumber = row + 1;
    const isRightSide = inTheRound ? true : rowNumber % 2 === 1;
    
    // Data storage mapping:
    // - col0 in storage = Column 1 (rightmost visually)
    // - col[width-1] in storage = Column width (leftmost visually)
    // 
    // For RS rows (or all in-the-round rows), read right to left visually:
    //   - Start at Column 1 (right) = col0, end at Column width (left) = col[width-1]
    //   - Read: col0, col1, ..., col[width-1] (use array as-is)
    //   - Written instructions should match this order (right to left as you knit)
    // 
    // For WS rows (flat knitting only), read left to right visually:
    //   - Start at Column width (left) = col[width-1], end at Column 1 (right) = col0
    //   - Read: col[width-1], col[width-2], ..., col0 (reverse the array)
    //   - Written instructions should match this order (left to right as you knit)
    const stitchesToProcess = isRightSide ? rowData : [...rowData].reverse();

    // Convert symbols to instruction strings for this row
    // On WS rows (flat knitting only), symbols need to be translated to their reverse/opposite
    // Charts show the right side appearance, so WS rows use reverse symbols
    // In-the-round: always use original symbols (always looking at right side)
    const instructionArray = stitchesToProcess.map((symbol) => {
      // Get the appropriate symbol for this row
      // - RS rows: use original symbol
      // - WS rows (flat only): use reverse symbol
      // - In-the-round: always use original symbol
      const symbolToUse = isRightSide ? symbol : getReverseSymbol(symbol);

      // Get symbol details for the symbol we're actually using
      const symbolDetails = getSymbolDetails(symbolToUse);
      if (symbolDetails) {
        if (abbreviate && symbolDetails.abbreviation) {
          return symbolDetails.abbreviation;
        }
        return symbolDetails.meaning;
      }

      // Fallback: if symbol not found, return the symbol itself
      // This should rarely happen if all symbols are properly defined in SYMBOL_KEY
      return symbolToUse || symbol;
    });

    // First, compress consecutive stitches
    const compressedInstruction = compressConsecutiveStitches(
      instructionArray.join(", "),
    );

    // Then group repeating sequences (but only if they're different stitch types)
    const rowInstruction = groupRepeatingSequences(compressedInstruction);
    if (rowInstruction) {
      rowInstructions.push({
        rowNumber: row + 1,
        isRightSide: isRightSide,
        instruction: rowInstruction,
      });
    }
  }

  // Group consecutive identical rows
  const groupedInstructions = groupConsecutiveRows(rowInstructions, inTheRound);

  return groupedInstructions;
};

/**
 * Group rows by detecting repeating sequences
 * @param {Array} rowInstructions - Array of objects with rowNumber, isRightSide, and instruction
 * @param {boolean} inTheRound - If true, don't add RS/WS labels
 * @returns {Array} Array of objects with rowLabel and instruction
 */
const groupConsecutiveRows = (rowInstructions, inTheRound = false) => {
  if (rowInstructions.length === 0) return [];

  const result = [];
  let i = 0;

  while (i < rowInstructions.length) {
    // Try to find repeating patterns starting from current position
    let bestPattern = null;
    let bestCount = 0;
    let bestLength = 0;

    // Check for patterns of different lengths (2 to remaining rows/2)
    for (
      let patternLength = 2;
      patternLength <= Math.floor((rowInstructions.length - i) / 2);
      patternLength++
    ) {
      if (i + patternLength > rowInstructions.length) break;

      const pattern = rowInstructions.slice(i, i + patternLength);

      let count = 1;
      let j = i + patternLength;

      // Count how many times this pattern repeats consecutively
      while (j + patternLength <= rowInstructions.length) {
        const nextPattern = rowInstructions.slice(j, j + patternLength);
        
        // Check if patterns match (instruction and side must match)
        const patternsMatch = pattern.every((row, idx) => {
          const nextRow = nextPattern[idx];
          const instructionMatch = row.instruction === nextRow.instruction;
          const sideMatch = inTheRound || row.isRightSide === nextRow.isRightSide;
          return instructionMatch && sideMatch;
        });

        if (patternsMatch) {
          count++;
          j += patternLength;
        } else {
          break;
        }
      }

      // If we found a repeating pattern, check if it's better than current best
      if (count > 1 && count * patternLength > bestLength) {
        bestPattern = pattern;
        bestCount = count;
        bestLength = count * patternLength;
      }
    }

    // If we found a good repeating pattern, group it
    if (bestPattern && bestCount > 1) {
      const startRow = bestPattern[0].rowNumber;
      const endRow = bestPattern[bestPattern.length - 1].rowNumber;
      
      // First, add the individual rows from the pattern
      for (const row of bestPattern) {
        const sideLabel = inTheRound ? "" : (row.isRightSide ? " (RS)" : " (WS)");
        result.push({
          rowLabel: `Row ${row.rowNumber}${sideLabel}`,
          instruction: row.instruction,
        });
      }
      
      // Then add the repeat instruction (bestCount - 1 because we already showed the first occurrence)
      // According to Craft Yarn Council standards, use "Rep" abbreviation
      const repeatCount = bestCount - 1;
      if (repeatCount > 0) {
        // Format: "Rep Rows 1 and 2" for two rows, or "Rep Rows 1-3" for ranges
        const rowRange = startRow === endRow 
          ? `Row ${startRow}`
          : endRow === startRow + 1
          ? `Rows ${startRow} and ${endRow}`
          : `Rows ${startRow}-${endRow}`;
        
        // Use "once", "twice" for 1-2, then "X times" for 3+
        const repeatText = repeatCount === 1 
          ? "once" 
          : repeatCount === 2 
          ? "twice" 
          : `${repeatCount} times`;
        
        result.push({
          rowLabel: `Rep ${rowRange}, ${repeatText}.`,
          instruction: "",
        });
      }
      
      i += bestLength;
    } else {
      // No repeating pattern found, add the current row
      const row = rowInstructions[i];
      const sideLabel = inTheRound ? "" : (row.isRightSide ? " (RS)" : " (WS)");
      result.push({
        rowLabel: `Row ${row.rowNumber}${sideLabel}`,
        instruction: row.instruction,
      });
      i++;
    }
  }

  return result;
};

/**
 * Group repeating stitch sequences in a row instruction
 * @param {string} instruction - The row instruction string (comma-separated)
 * @returns {string} Instruction with grouped repeating sequences (comma-separated)
 */
const groupRepeatingSequences = (instruction) => {
  if (!instruction) return instruction;

  // Split by commas to get individual stitches
  const stitches = instruction.split(",").map((s) => s.trim()).filter((s) => s);
  if (stitches.length === 0) return instruction;

  const result = [];
  let i = 0;

  while (i < stitches.length) {
    // Try to find repeating patterns starting from current position
    let bestPattern = null;
    let bestCount = 0;
    let bestLength = 0;

    // Check for patterns of different lengths (2 to stitches.length/2)
    for (
      let patternLength = 2;
      patternLength <= Math.floor(stitches.length / 2);
      patternLength++
    ) {
      if (i + patternLength > stitches.length) break;

      const pattern = stitches.slice(i, i + patternLength);

      // Skip if pattern contains only one unique stitch type (already compressed)
      const uniqueStitches = [...new Set(pattern)];
      if (uniqueStitches.length === 1) {
        continue;
      }

      let count = 1;
      let j = i + patternLength;

      // Count how many times this pattern repeats
      while (j + patternLength <= stitches.length) {
        const nextPattern = stitches.slice(j, j + patternLength);
        if (arraysEqual(pattern, nextPattern)) {
          count++;
          j += patternLength;
        } else {
          break;
        }
      }

      // If we found a repeating pattern, check if it's better than current best
      if (count > 1 && count * patternLength > bestLength) {
        bestPattern = pattern;
        bestCount = count;
        bestLength = count * patternLength;
      }
    }

    // If we found a good repeating pattern, group it
    // According to Craft Yarn Council standards, use brackets [ ] with "times" format
    if (bestPattern && bestCount > 1) {
      const patternStr = bestPattern.join(", ");
      result.push(`[${patternStr}] ${bestCount} times`);
      i += bestLength;
    } else {
      // No repeating pattern found, add the current stitch
      result.push(stitches[i]);
      i++;
    }
  }

  return result.join(", ");
};

/**
 * Compress consecutive stitches in an instruction string
 * @param {string} instruction - The instruction string (comma-separated)
 * @returns {string} Instruction with compressed consecutive stitches (comma-separated)
 */
const compressConsecutiveStitches = (instruction) => {
  if (!instruction) return instruction;

  // Handle grouped patterns like "[Knit, Purl] 5 times" - don't compress these
  if (instruction.includes("[") && instruction.includes("]") && instruction.includes("times")) {
    return instruction;
  }

  // Split by commas to get individual stitches
  const stitches = instruction.split(",").map((s) => s.trim()).filter((s) => s);
  if (stitches.length === 0) return instruction;

  const result = [];
  let i = 0;

  while (i < stitches.length) {
    const currentStitch = stitches[i];
    let count = 1;
    let j = i + 1;

    // Count consecutive identical stitches
    while (j < stitches.length && stitches[j] === currentStitch) {
      count++;
      j++;
    }

    // For Knit and Purl (and their abbreviations), always include the count
    // For other stitches, only include count if greater than 1
    const isKnitOrPurl = 
      currentStitch === "Knit" || 
      currentStitch === "Purl" || 
      currentStitch === "K" || 
      currentStitch === "P";
    
    if (isKnitOrPurl || count > 1) {
      result.push(`${currentStitch} ${count}`);
    } else {
      result.push(currentStitch);
    }

    i = j;
  }

  return result.join(", ");
};

/**
 * Check if two arrays are equal
 * @param {Array} a - First array
 * @param {Array} b - Second array
 * @returns {boolean} True if arrays are equal
 */
const arraysEqual = (a, b) => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

/**
 * Convert compiled pattern instructions to plain text string
 * @param {Array|string} instructions - Structured instructions array or plain text string
 * @param {boolean} inTheRound - Unused parameter (kept for backward compatibility)
 * @returns {string} Plain text instructions
 */
export const compilePatternToText = (instructions, inTheRound = false) => {
  // If it's already a string (backward compatibility), return it
  if (typeof instructions === "string") {
    return instructions;
  }

  // If it's an array of structured instructions, convert to text
  if (Array.isArray(instructions)) {
    return instructions
      .map((item) => {
        // If instruction is empty (e.g., repeat rows), just use the rowLabel as-is
        if (!item.instruction || item.instruction.trim() === "") {
          return item.rowLabel;
        }
        // Otherwise, format as "RowLabel: instruction."
        return `${item.rowLabel}: ${item.instruction}.`;
      })
      .join("\n");
  }

  return "";
};

/**
 * Get chart data from a rendered grid
 * @param {Object} chart - Chart object
 * @returns {Object} Pattern data object
 */
export const getChartData = (chart) => {
  if (!chart || !chart.rows) {
    return null;
  }

  const { height, width, rows } = chart;
  const patternData = {};

  for (let row = 0; row < height; row++) {
    const rowKey = `row${row}`;
    if (rows[rowKey]) {
      patternData[rowKey] = [...rows[rowKey]];
    }
  }

  return {
    pattern: {
      title: "",
      chart: {
        height,
        width,
        rows: patternData,
      },
    },
  };
};

/**
 * Create an empty chart grid
 * @param {number} height - Grid height
 * @param {number} width - Grid width
 * @returns {Object} Empty chart object
 */
export const createEmptyChart = (height, width) => {
  const rows = {};

  for (let row = 0; row < height; row++) {
    rows[`row${row}`] = Array(width).fill(DEFAULT_SYMBOL);
  }

  return {
    height,
    width,
    rows,
  };
};
